import { IMPORT_FIELDS, IMPORT_FIELD_KEYS, type ImportFieldKey, type ImportJobDTO, type UpdateImportBody, unmappedNoteLines } from '@hearth/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { Button, Card, CardHeader, Checkbox, Select, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { pluralize } from '../../lib/format';
import { ImportOptionCards, type ImportOptions, validCountry } from './ImportOptions';

const GROUP_LABELS: Record<(typeof IMPORT_FIELDS)[ImportFieldKey]['group'], string> = {
  business: 'Business',
  contact: 'Contact',
  assignment: 'Assignment',
};

/** Step 2, when the guess wasn’t enough (or was asked to be checked): which column is which, plus defaults applied to every row. */
export function ImportMapping({ job }: { job: ImportJobDTO }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const initial = Object.keys(job.columnMapping).length > 0 ? job.columnMapping : job.suggestedMapping;
  const [mapping, setMapping] = useState<Partial<Record<string, ImportFieldKey>>>(initial);
  const [options, setOptions] = useState<ImportOptions>({
    defaultOwnerId: job.defaultOwnerId,
    defaultSourceId: job.defaultSourceId,
    defaultTagIds: job.defaultTagIds,
    defaultCountry: job.defaultCountry,
    duplicatePolicy: job.duplicatePolicy,
    defaultConsentBasis: job.defaultConsentBasis,
    businessKind: job.businessKind,
  });
  const [appendLeftovers, setAppendLeftovers] = useState(job.appendUnmappedToNotes);
  const [ticked, setTicked] = useState<Set<string>>(() => new Set());

  const used = new Map<ImportFieldKey, string>();
  for (const [header, field] of Object.entries(mapping)) if (field) used.set(field, header);
  const nameMapped = used.has('name');
  // Columns that hold values but have no field (in the matching as it stands now): the wizard says so instead of
  // dropping them quietly, and shows what their values would add to the notes.
  const skipped = job.filledColumns.filter((column) => !mapping[column.header]);
  const preview = leftoverPreview(job, mapping);
  // Judged against the field chosen here, not the saved one, so the warning follows the dropdown. The business name
  // is free text: whatever the column holds is the name.
  const issueFor = (header: string): string | null => {
    const field = mapping[header];
    return field && field !== 'name' ? (job.columnIssues[header]?.[field] ?? null) : null;
  };
  const problems = job.headers.filter((header) => issueFor(header) !== null);
  const allTicked = job.headers.length > 0 && job.headers.every((header) => ticked.has(header));

  const toggle = (header: string, checked: boolean) =>
    setTicked((current) => {
      const next = new Set(current);
      if (checked) next.add(header);
      else next.delete(header);
      return next;
    });
  const unmapTicked = () => {
    setMapping((current) => {
      const next = { ...current };
      for (const header of ticked) delete next[header];
      return next;
    });
    setTicked(new Set());
  };

  const save = useMutation({
    mutationFn: async () => {
      const columnMapping: UpdateImportBody['columnMapping'] = {};
      for (const [header, field] of Object.entries(mapping)) if (field) columnMapping[header] = field;
      await api.patch(`/api/imports/${job.id}`, { columnMapping, ...options, appendUnmappedToNotes: appendLeftovers } satisfies UpdateImportBody);
      return api.post<ImportJobDTO>(`/api/imports/${job.id}/validate`);
    },
    onSuccess: (next) => queryClient.setQueryData(['imports', job.id], next),
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
      <Card>
        <CardHeader
          title="Match your columns"
          description="We guessed from the header names — check each one. The leads are created as soon as you continue; anything unsure is left for you to review. Columns left unmatched are kept in the import report."
        />
        {(problems.length > 0 || ticked.size > 0) && (
          <div
            className={clsx(
              'flex flex-wrap items-center gap-2 border-b px-4 py-2 text-[13px]',
              ticked.size > 0 ? 'border-ember-200 bg-ember-50' : 'border-amber-200 bg-amber-50',
            )}
          >
            {ticked.size > 0 ? (
              <span className="font-medium text-ember-900">{ticked.size} selected</span>
            ) : (
              <span className="text-amber-900">
                {problems.length === 1 ? '1 column doesn’t fit its field' : `${problems.length} columns don’t fit their fields`}
              </span>
            )}
            {problems.length > 0 && (
              <Button size="sm" variant="ghost" icon={AlertTriangle} onClick={() => setTicked(new Set(problems))}>
                Select problem columns ({problems.length})
              </Button>
            )}
            {ticked.size > 0 && (
              <div className="ml-auto flex flex-wrap items-center gap-1.5">
                <Button size="sm" onClick={unmapTicked}>
                  Don’t import selected
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setTicked(new Set())}>
                  Clear
                </Button>
              </div>
            )}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs text-slate-500">
                <th className="w-9 py-2 pl-4">
                  <Checkbox
                    aria-label="Select all columns"
                    checked={allTicked}
                    indeterminate={ticked.size > 0 && !allTicked}
                    onChange={(event) => setTicked(event.target.checked ? new Set(job.headers) : new Set())}
                  />
                </th>
                <th className="px-4 py-2 font-medium">Column in your file</th>
                <th className="px-4 py-2 font-medium">Examples</th>
                <th className="px-4 py-2 font-medium">Hearth field</th>
              </tr>
            </thead>
            <tbody>
              {job.headers.map((header) => {
                const issue = issueFor(header);
                return (
                  <tr key={header} className="border-b border-slate-50">
                    <td className="py-2 pl-4">
                      <Checkbox aria-label={`Select ${header}`} checked={ticked.has(header)} onChange={(event) => toggle(header, event.target.checked)} />
                    </td>
                    <td className="px-4 py-2 font-medium text-slate-800">{header}</td>
                    <td className="max-w-64 truncate px-4 py-2 text-slate-500">
                      {job.sample
                        .map((row) => row[header])
                        .filter(Boolean)
                        .slice(0, 3)
                        .join(' · ') || '—'}
                    </td>
                    <td className="px-4 py-2">
                      <Select
                        size="sm"
                        aria-label={`Field for ${header}`}
                        value={mapping[header] ?? ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          const next = { ...mapping };
                          if (value === '') delete next[header];
                          else next[header] = IMPORT_FIELD_KEYS.find((key) => key === value);
                          setMapping(next);
                        }}
                      >
                        <option value="">Don’t import</option>
                        {(['business', 'contact', 'assignment'] as const).map((group) => (
                          <optgroup key={group} label={GROUP_LABELS[group]}>
                            {IMPORT_FIELD_KEYS.filter((key) => IMPORT_FIELDS[key].group === group).map((key) => (
                              <option key={key} value={key} disabled={used.has(key) && used.get(key) !== header}>
                                {IMPORT_FIELDS[key].label}
                                {IMPORT_FIELDS[key].hint ? ` (${IMPORT_FIELDS[key].hint})` : ''}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </Select>
                      {issue && (
                        <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
                          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
                          {issue}
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {skipped.length > 0 && (
          <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
            <p className="font-medium">
              {skipped.length} column{skipped.length === 1 ? '' : 's'} match no field
            </p>
            <ul className="mt-1 list-disc pl-5">
              {skipped.map((column) => (
                <li key={column.header}>
                  <code>{column.header}</code> — {column.filledRows} value{column.filledRows === 1 ? '' : 's'} in the first rows
                  {column.sample && <> · e.g. “{column.sample}”</>}
                </li>
              ))}
            </ul>
            <label className="mt-2 flex items-start gap-2">
              <input type="checkbox" className="mt-0.5" checked={appendLeftovers} onChange={(event) => setAppendLeftovers(event.target.checked)} />
              <span>
                Keep them in each lead's notes as “Column: value” lines
                {appendLeftovers && preview && <pre className="mt-1 max-h-32 overflow-auto rounded bg-surface/70 p-2 text-xs">{preview}</pre>}
              </span>
            </label>
            <p className="mt-1">
              Either map a column to a Hearth field above, or leave it here — several columns can share{' '}
              <strong>Notes / description</strong> and their values are appended together.
            </p>
          </div>
        )}
      </Card>

      <div className="space-y-4">
        <ImportOptionCards value={options} onChange={(patch) => setOptions((current) => ({ ...current, ...patch }))} />
        <Button
          variant="primary"
          iconRight={ArrowRight}
          className="w-full"
          disabled={!nameMapped || !validCountry(options.defaultCountry)}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          {nameMapped ? `Import ${pluralize(job.totalRows, 'row')}` : 'Match a column to “Business name”'}
        </Button>
      </div>
    </div>
  );
}

/** What the first sampled row with leftovers would get in its notes, under the matching as it stands now. */
function leftoverPreview(job: ImportJobDTO, mapping: Partial<Record<string, ImportFieldKey>>): string | null {
  for (const row of job.sample) {
    const lines = unmappedNoteLines(row, mapping, job.headers);
    if (lines.length > 0) return lines.join('\n');
  }
  return null;
}
